import { createContext, useState, useCallback, useMemo, useEffect } from 'react';
import { logger } from '../lib/utils/logger';
import { useAuth } from '../lib/hooks/useAuth';
import { defaultAccountAPI } from '../lib/services/accountApi';
import { showError } from '../lib/services/errorService';

/**
 * Tenant context for multi-tenancy support.
 *
 * Provides tenant selection functionality for GLOBAL_ADMIN users.
 * Regular users are fixed to their assigned tenant.
 */
export const TenantContext = createContext(null);

/**
 * TenantProvider component
 * Wraps application and provides tenant state management
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export function TenantProvider({ children }) {
  const { user, isGlobalAdmin } = useAuth();

  // GLOBAL_ADMIN can select a different tenant
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [autoSelectedTenantId, setAutoSelectedTenantId] = useState(null);

  // Auto-select first tenant for GLOBAL_ADMIN without a real tenantId
  useEffect(() => {
    const autoSelectTenant = async () => {
      const hasRealTenant = user?.tenantId;
      if (isGlobalAdmin() && !hasRealTenant && !selectedTenantId && !autoSelectedTenantId) {
        try {
          const page = await defaultAccountAPI.getTenants({ page: 0, size: 1 });
          const tenants = page?.content || [];
          if (tenants.length > 0) {
            setAutoSelectedTenantId(tenants[0].tenantId);
            logger.info('[TenantContext] Auto-selected tenant:', tenants[0].tenantId);
          }
        } catch (err) {
          logger.error('[TenantContext] Failed to auto-select tenant:', err);
          showError(err, '테넌트 목록을 불러올 수 없습니다. 새로고침해 주세요.');
        }
      }
    };
    autoSelectTenant();
  }, [user, isGlobalAdmin, selectedTenantId, autoSelectedTenantId]);

  /**
   * Get the effective tenant ID for API calls.
   * - GLOBAL_ADMIN: Uses selectedTenantId if set, otherwise autoSelectedTenantId, otherwise own tenantId
   * - Other users: Always uses their assigned tenantId
   */
  const effectiveTenantId = useMemo(() => {
    if (isGlobalAdmin()) {
      // Priority: manual selection > auto-selected > own tenantId
      return selectedTenantId || autoSelectedTenantId || user?.tenantId || null;
    }
    return user?.tenantId || null;
  }, [user, selectedTenantId, autoSelectedTenantId, isGlobalAdmin]);

  /**
   * Select a tenant (GLOBAL_ADMIN only).
   * @param {string} tenantId - Tenant ID to select
   */
  const selectTenant = useCallback(
    (tenantId) => {
      if (isGlobalAdmin()) {
        setSelectedTenantId(tenantId);
      }
    },
    [isGlobalAdmin]
  );

  /**
   * Reset tenant selection to user's own tenant.
   */
  const resetTenant = useCallback(() => {
    setSelectedTenantId(null);
  }, []);

  /**
   * Check if current user can select a different tenant.
   */
  const canSelectTenant = useMemo(() => {
    return isGlobalAdmin();
  }, [isGlobalAdmin]);

  /**
   * Check if a different tenant is currently selected.
   */
  const isOtherTenantSelected = useMemo(() => {
    const currentTenantId = selectedTenantId || autoSelectedTenantId;
    return currentTenantId && currentTenantId !== user?.tenantId;
  }, [selectedTenantId, autoSelectedTenantId, user]);

  // Memoize context value
  const value = useMemo(
    () => ({
      effectiveTenantId,
      selectedTenantId,
      selectTenant,
      resetTenant,
      canSelectTenant,
      isOtherTenantSelected,
    }),
    [
      effectiveTenantId,
      selectedTenantId,
      selectTenant,
      resetTenant,
      canSelectTenant,
      isOtherTenantSelected,
    ]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export default TenantContext;
